// Quanto tempo o app ficou FORA da tela (0.66). Usado pelo bloqueio do app e pelo Diário.
//
// Por que existe: no Android, o React Native avisa "background" quando a tela só perde o foco
// (janela de biometria, pedido de permissão, app em janela ou tela dividida, seletor de arquivos).
// No iPhone isso já vem separado ("inactive" não é "background").
// - Android com o app novo: o módulo nativo MpVisivel conta as telas visíveis; só conta como fora
//   quando nenhuma tela do app ficou visível (onStop).
// - Android com o app antigo (só a atualização automática): perdas de foco curtas não contam.
// - Galeria, câmera, arquivos, compartilhar, "Abrir com..." e a biometria abertos PELO PRÓPRIO APP
//   (semBloqueio) não contam como sair, até 10 minutos.
import { AppState, Platform } from 'react-native';

type Nativo = { paradoEm(): number; visivel(): boolean };
let nativo: Nativo | null = null;
if (Platform.OS === 'android') {
  try { nativo = require('expo-modules-core').requireOptionalNativeModule('MpVisivel'); } catch { nativo = null; }
}
export const temDeteccaoNativa = () => !!nativo;

const LIMITE_EXTERNO = 10 * 60000;   // ação externa aberta pelo app: não conta como saída até 10 minutos
const CURTO_SEM_NATIVO = 3000;        // Android sem o módulo: menos de 3 s fora é só perda de foco

let externos = 0;
let externoAte = 0;
const emExterno = () => externos > 0 || Date.now() < externoAte;
/** true enquanto o app tem aberta uma tela do sistema (galeria, câmera, arquivos, compartilhar...) e logo depois de voltar dela. */
export const acaoExternaAberta = emExterno;

/** Envolve uma ação que abre outra tela do sistema (galeria, câmera, arquivos, compartilhar, biometria...).
 *  Na volta, o app não pede a senha e o Diário continua aberto. */
export async function semBloqueio<T>(fn: () => Promise<T>): Promise<T> {
  externos++;
  try { return await fn(); }
  finally { externos = Math.max(0, externos - 1); externoAte = Date.now() + 1500; }
}

type Volta = (foraMs: number) => void;
const ouvintes = new Set<Volta>();
/** Avisa quando o app volta para a tela, com quanto tempo ficou fora DE VERDADE (0 = não saiu). */
export function aoVoltar(f: Volta) { ouvintes.add(f); return () => { ouvintes.delete(f); }; }

let pausaEm = 0;
let pausaExterna = false;
let foraDesde = 0;   // quando saiu de verdade (0 = está na tela)
/** Desde quando o app está fora da tela (0 = está na tela). */
export const saiuEm = () => foraDesde;

function calcular(): number {
  const agora = Date.now();
  let fora = agora - pausaEm;
  if (nativo) {
    let parado = 0;
    try { parado = Number(nativo.paradoEm()) || 0; } catch { parado = 0; }
    // nenhuma tela do app parou depois da pausa: foi só perda de foco
    fora = parado && parado >= pausaEm - 300 ? agora - parado : 0;
  } else if (Platform.OS === 'android' && fora < CURTO_SEM_NATIVO) fora = 0;
  if ((pausaExterna || emExterno()) && fora < LIMITE_EXTERNO) fora = 0;
  return Math.max(0, fora);
}

if (Platform.OS !== 'web') {
  AppState.addEventListener('change', s => {
    if (s === 'background') {
      if (!pausaEm) { pausaEm = Date.now(); pausaExterna = emExterno(); foraDesde = pausaEm; }
    } else if (s === 'active') {
      if (!pausaEm) return;
      const fora = calcular();
      pausaEm = 0; pausaExterna = false; foraDesde = 0;
      ouvintes.forEach(f => { try { f(fora); } catch { /* segue */ } });
    }
    // "inactive" (iPhone: Central de Controle, notificações, Face ID): não é sair do app
  });
}
